import RoleColorPicker from "./RoleColorPicker";
import React, { useEffect } from "react";
import Drawer from "@mui/material/Drawer";
import DrawerHeader from "../requests/shared/helpers/DrawerHeader";
import DrawerFooterContainerLarge from "../requests/shared/helpers/DrawerFooterContainerLarge";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import Select from "@mui/material/Select";
import TextField from "@mui/material/TextField";
import ButtonFilled from "../requests/shared/helpers/ButtonFilled";
import ButtonOutlined from "../requests/shared/helpers/ButtonOutlined";
import LoadingButton from "@mui/lab/LoadingButton";
import SaveIcon from "@mui/icons-material/Save";
import Checkbox from "@mui/material/Checkbox";
import ListItemText from "@mui/material/ListItemText";

import {
  ROLE_REQUIRED_CERTIFICATE_OPTIONS,
  CERTIFICATE_TYPE_LABELS,
} from "../../../utilities/certificateTypes";

const NON_COVERAGE_OPTIONS = [
  { value: "training", label: "Training" },
  { value: "admin", label: "Admin" },
];

function RoleSettingsDrawer(props) {
  const [state, setState] = React.useState(props.modalData);

  // Requireable certificate types = uploadable catalog types, excluding the
  // generic "Other" bucket (a role can't meaningfully require an "Other"
  // certificate),
  // emergency_contact (non-uploadable, already filtered), and any type hidden from
  // the Certificates overview (hide and require are mutually exclusive). Falls back to
  // the built-in role options when the catalog hasn't loaded.
  const baseRequiredCertificateOptions = React.useMemo(() => {
    const catalog = props.certificateTypes;
    if (Array.isArray(catalog) && catalog.length > 0) {
      return catalog
        .filter((t) => t.uploadable && t.value !== "other" && !t.overview_hidden)
        .map((t) => ({ value: t.value, label: t.label }));
    }
    return ROLE_REQUIRED_CERTIFICATE_OPTIONS;
  }, [props.certificateTypes]);

  // Keep any already-required slug that is no longer in the catalog (archived custom
  // type, or break_waiver after the flag was disabled) visible + uncheckable, so it
  // isn't an invisible MUI out-of-range value that silently re-persists on save.
  const requiredCertificateOptions = React.useMemo(() => {
    const selected = state?.required_certificate_types || [];
    const known = new Set(baseRequiredCertificateOptions.map((o) => o.value));
    const extras = selected
      .filter((slug) => !known.has(slug))
      .map((slug) => ({
        value: slug,
        label:
          (Array.isArray(props.certificateTypes) &&
            props.certificateTypes.find((t) => t.value === slug)?.label) ||
          CERTIFICATE_TYPE_LABELS[slug] ||
          slug,
      }));
    return [...baseRequiredCertificateOptions, ...extras];
  }, [baseRequiredCertificateOptions, state?.required_certificate_types, props.certificateTypes]);

  useEffect(() => {
    if (!props.modalData) {
      setState(props.modalData);
      return;
    }

    const nextState = {
      ...props.modalData,
      required_certificate_types: props.modalData?.required_certificate_types || [],
    };

    if (
      nextState?.exclude_from_labor &&
      !nextState?.non_coverage_classification
    ) {
      nextState.non_coverage_classification = NON_COVERAGE_OPTIONS[0].value;
    }

    setState(nextState);
  }, [props.modalData]);

  function handleChange(evt) {
    const { name, value } = evt.target;
    setState({
      ...state,
      [name]: value,
    });
  }

  function handleColorSelection(color) {
    setState({
      ...state,
      color: color,
    });
  }

  function handleExcludeFromLaborChecked(evt) {
    const { checked } = evt.target;
    setState((prevState) => ({
      ...prevState,
      exclude_from_labor: checked,
      non_coverage_classification: checked
        ? prevState?.non_coverage_classification || NON_COVERAGE_OPTIONS[0].value
        : null,
    }));
  }

  function handleRequiredCertificatesChange(evt) {
    const { value } = evt.target;
    setState((prevState) => ({
      ...prevState,
      required_certificate_types:
        typeof value === "string" ? value.split(",") : value,
    }));
  }

  function handleSubmit() {
    // console.log("SUBMITTING: ", state);
    let edit_type = "edit";
    if (!props.modalEdit) {
      edit_type = "create";
    }
    if (state.exclude_from_labor && !state.non_coverage_classification) {
      alert("Please select a non-coverage type for this role.");
      return;
    }
    props.edit_role(state, state.color, edit_type);
  }

  useEffect(() => {
    if (!props.isSavingData) {
      props.onClose();
    }
  }, [props.isSavingData]);

  // console.log("STATE", state);

  return (
    <Drawer anchor="right" open={props.open} onClose={props.onClose}>
      <div className="w-[450px] h-full bg-slate-50 relative">
        <div className="max-h-screen overflow-auto">
          <DrawerHeader
            title={`${props.modalEdit ? "Edit" : "Add"} role`}
            onClose={props.onClose}
          />
          <div className="w-full px-6">
            <div className="mt-7">
              <p className="text-sm text-slate-500">Location</p>
              <div className="mt-1">
                <div className="bg-slate-200 text-slate-400 rounded-[8px] h-[45px] px-4 flex items-center">
                  <p>{props.store_name}</p>
                </div>
              </div>
            </div>
            <div className="mt-7">
              <p className="text-sm text-slate-500">Name</p>
              {props.permissionRole !== "company_manager" ? (
                <div className="mt-1">
                  <TextField
                    placeholder="Server"
                    fullWidth
                    variant="outlined"
                    size="small"
                    name="role_name"
                    value={state.role_name}
                    onChange={handleChange}
                    InputProps={{
                      sx: {
                        backgroundColor: "white",
                        borderRadius: "8px",
                        padding: "5px",
                        height: "45px",
                        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#6d28d9",
                        },
                      },
                    }}
                  />
                </div>
              ) : (
                <div className="mt-1">
                  <div className="bg-slate-200 text-slate-400 rounded-[8px] h-[45px] px-4 flex items-center">
                    <p>{state.role_name}</p>
                  </div>
                </div>
              )}
            </div>
            <div className="mt-7">
              <p className="text-sm text-slate-500">Department</p>
              {props.permissionRole !== "company_manager" ? (
                <div className="mt-1">
                  <FormControl fullWidth size="small">
                    <Select
                      labelId="role-department"
                      id="role-department"
                      name="role_type_id"
                      value={state.role_type_id}
                      onChange={handleChange}
                      displayEmpty
                      renderValue={(selected) => {
                        // Find the department with the matching id and return its role_type
                        const selectedDept = props.departments.find(
                          (dept) => dept.id === selected
                        );
                        return selectedDept ? selectedDept.role_type : "Select a department";
                      }}
                      sx={{
                        backgroundColor: "white",
                        borderRadius: "8px",
                        padding: "5px",
                        height: "45px",
                        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#6d28d9",
                        },
                      }}
                    >
                      {props.departments.map((v, i) => (
                        <MenuItem key={v.id} value={v.id}>
                          {v.role_type}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </div>
              ) : (
                <div className="mt-1">
                  <div className="bg-slate-200 text-slate-400 rounded-[8px] h-[45px] px-4 flex items-center">
                    <p>
                      {
                        props.departments.find(
                          (dept) => dept.id === state.role_type_id
                        )?.role_type
                      }
                    </p>
                  </div>
                </div>
              )}
            </div>
            <div className="mt-7">
              {props.permissionRole !== "company_manager" ? (
                <div className="flex items-center">
                  <Checkbox
                    name="exclude_from_labor"
                    checked={state.exclude_from_labor}
                    onChange={handleExcludeFromLaborChecked}
                    sx={{
                      color: "#6d28d9",
                      "&.Mui-checked": {
                        color: "#6d28d9",
                      },
                    }}
                  />
                  <p className="text-sm text-slate-500 ml-1">
                    Exclude role from labor calculations?
                  </p>
                </div>
              ) : (
                <div className="flex items-center">
                  <Checkbox
                    name="exclude_from_labor"
                    checked={state.exclude_from_labor}
                    disabled
                  />
                  <p className="text-sm text-slate-500 ml-1">
                    Exclude role from labor calculations?
                  </p>
                </div>
              )}
            </div>
            {state.exclude_from_labor && (
              <div className="mt-4">
                <p className="text-sm text-slate-500">Non-coverage type</p>
                <div className="mt-1">
                  <FormControl fullWidth size="small">
                    <Select
                      name="non_coverage_classification"
                      value={state.non_coverage_classification || ""}
                      onChange={handleChange}
                      displayEmpty
                      renderValue={(selected) => {
                        if (!selected) {
                          return "Select a type";
                        }
                        const selectedOption = NON_COVERAGE_OPTIONS.find(
                          (option) => option.value === selected
                        );
                        return selectedOption ? selectedOption.label : selected;
                      }}
                      sx={{
                        backgroundColor: "white",
                        borderRadius: "8px",
                        padding: "5px",
                        height: "45px",
                        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#6d28d9",
                        },
                      }}
                    >
                      {NON_COVERAGE_OPTIONS.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </div>
              </div>
            )}
            <div className="mt-7">
              <p className="text-sm text-slate-500">Required certificates</p>
              <div className="mt-1">
                <FormControl fullWidth size="small">
                  <Select
                    multiple
                    name="required_certificate_types"
                    value={state.required_certificate_types || []}
                    onChange={handleRequiredCertificatesChange}
                    displayEmpty
                    renderValue={(selected) => {
                      if (!selected || selected.length === 0) {
                        return "No required documents";
                      }
                      return requiredCertificateOptions
                        .filter((option) => selected.includes(option.value))
                        .map((option) => option.label)
                        .join(", ");
                    }}
                    sx={{
                      backgroundColor: "white",
                      borderRadius: "8px",
                      padding: "5px",
                      minHeight: "45px",
                      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#6d28d9",
                      },
                    }}
                  >
                    {requiredCertificateOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        <Checkbox
                          checked={(state.required_certificate_types || []).includes(option.value)}
                          sx={{
                            color: "#6d28d9",
                            "&.Mui-checked": {
                              color: "#6d28d9",
                            },
                          }}
                        />
                        <ListItemText primary={option.label} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </div>
            </div>
            <div className="mt-7">
              <p className="text-sm text-slate-500">{props.companyScope ? "Company default color" : "Color"}</p>
              <div className="mt-2"><RoleColorPicker value={state.color} onChange={handleColorSelection} disabled={props.isSavingData} label={props.companyScope ? "Company default color" : "Role color"} /></div>
            </div>
          </div>
          <DrawerFooterContainerLarge>
            <div className="w-full">
              <LoadingButton
                fullWidth
                loading={props.isSavingData}
                loadingPosition="end"
                endIcon={<SaveIcon />}
                variant="contained"
                onClick={() => handleSubmit()}
                disabled={!state.role_type_id}
                sx={{
                  textTransform: "none",
                  borderRadius: "12px",
                  paddingY: "14px",
                  backgroundColor: "#6d28d9",
                  "&:hover": {
                    backgroundColor: "#5b21b6",
                  },
                  fontSize: "16px",
                }}
              >
                Save
              </LoadingButton>
            </div>
            <div className="w-full mt-3">
              <ButtonOutlined text="Cancel" onClick={props.onClose} />
            </div>
          </DrawerFooterContainerLarge>
          <div className="h-[200px]"></div>
        </div>
      </div>
    </Drawer>
  );
}

export default RoleSettingsDrawer;
